'use client';

/**
 * AnswerDock: the one object a member answers through, for the whole question
 * (owner-approved design brief 2026-10-02, WL-20261002-a7e5b6).
 *
 * It keeps its identity for the whole question and changes shape:
 *
 *   voice      a wide pill: the record button, its status line, "Type instead"
 *   listening  the same pill: the button has become Stop, a live meter speaks
 *   text       a card: the answer field, "Speak instead", Send
 *   rest       a small disc: the middle of the screen is busy (the three
 *              dots, or a question or reflection still landing). A faint node
 *              network fires inside it, quiet enough not to pull the eye
 *   reflection the disc opens into Continue (the reflection itself reads in
 *              the middle), so the main action never moves
 *
 * The dock is anchored to the bottom of the screen by its parent (owner
 * feedback 2026-10-05: an input that lands somewhere different on every
 * question is a bad experience), so every change of shape happens in the
 * same place. Waiting is shown in the middle, never here (owner feedback
 * 2026-10-06: after Stop the eye stayed on the dock and missed the reflection).

 *
 * Motion, in three beats (DESIGN.md, Motion):
 *   1. the outgoing contents fade out (80ms, ease-in);
 *   2. the shell reshapes through a layout (FLIP) animation, 220ms ease-out
 *      quart: transforms only, never an animated width or height;
 *   3. the incoming contents fade in from 98% scale (160ms), starting before the
 *      reshape ends.
 *
 * `voice` and `listening` share one content group, so the record button is the
 * same element throughout and its mic-to-stop icon morph finally plays.
 * `MotionConfig reducedMotion="user"` (app/MotionProvider.tsx) already turns the
 * transform and layout parts off for reduced-motion members, leaving the fades.
 */

import type { ReactNode } from 'react';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT_QUART } from '@core/lib/constants';
import { NeuralMark } from '@/components/session-phases/AnswerDockParts';

export type AnswerDockState = 'voice' | 'listening' | 'text' | 'rest' | 'reflection';

/** ease-out quart, the DESIGN.md default for anything arriving. */
export const DOCK_EASE_OUT = EASE_OUT_QUART;
/** ease-in quart, for what leaves (exits run faster than entrances). */
const DOCK_EASE_IN = [0.5, 0, 0.75, 0] as const;

/** Seconds, as Motion takes them. */
export const DOCK_MOTION = {
  exit: 0.08,
  reshape: 0.22,
  enter: 0.16,
  enterDelay: 0.08,
} as const;

/**
 * Shell radius per shape. The pills are 80px tall (a 64px button inside 8px of
 * padding), so 40 is fully round, and the resting disc stays a circle; the card
 * takes the `lg` radius (20px); Continue's footprint takes Continue's own 12px,
 * so the disc opens into exactly the button's shape. Literal pixels, not the
 * CSS variables: Motion corrects a radius it animates for the shell's scale
 * during the reshape, and it can only do that for a number it can read.
 */
const SHELL_RADIUS = { pill: 40, card: 20, action: 12 } as const;

/**
 * Rest shadow (DESIGN.md `--shadow-sm`), with a terracotta hairline while
 * listening so the whole dock reads as recording. Literal for the same reason as
 * the radius: Motion scale-corrects a shadow it can parse.
 */
const SHELL_SHADOW = {
  rest: '0 0 0 1px rgba(26, 35, 50, 0.06), 0 1px 3px rgba(26, 35, 50, 0.08)',
  listening: '0 0 0 1.5px rgba(224, 122, 95, 0.45), 0 1px 3px rgba(26, 35, 50, 0.08)',
  // Same structure, fully transparent, so Motion can fade the shell away.
  none: '0 0 0 0px rgba(26, 35, 50, 0), 0 0px 0px rgba(26, 35, 50, 0)',
} as const;

type DockGroup = 'speak' | 'rest' | 'text' | 'action';

function dockGroup(state: AnswerDockState): DockGroup {
  if (state === 'text') return 'text';
  if (state === 'reflection') return 'action';
  if (state === 'rest') return 'rest';
  return 'speak';
}

const SHELL_LAYOUT: Record<DockGroup, string> = {
  speak: 'w-full flex-row items-center gap-3 p-2 pr-2',
  rest: 'w-fit p-0',
  text: 'w-full flex-col p-4',
  // The shell steps back to nothing around Continue: one filled primary, not a
  // button in a pill. No padding, so the disc reshapes to the button's own
  // footprint; nothing clips here, so its focus ring and hover glow show.
  action: 'w-fit flex-row items-center justify-center p-0',
};

/** The resting disc: smaller than the record button, so it reads as quiet. */
const REST_DISC = 'flex h-12 w-12 items-center justify-center';

export interface AnswerDockProps {
  state: AnswerDockState;
  /**
   * The leading mark in the speaking pill: the record button. It sits in one
   * persistent slot, so the same button turns from mic to Stop in place.
   * Ignored in every other shape.
   */
  mark?: ReactNode;
  /**
   * While listening: whether the microphone pipeline is consuming audio yet.
   * Exposed as `data-recording-interface` / `data-capture-ready`, the contract
   * the survey harness reads to know a recording can be spoken into
   * (survey-harness/browser/screenReader.ts).
   */
  captureReady?: boolean;
  /** The current shape's contents. The resting disc has none. */
  children?: ReactNode;
}

export function AnswerDock({ state, mark, captureReady, children }: AnswerDockProps) {
  const group = dockGroup(state);
  const isCard = group === 'text';
  const radius = isCard ? SHELL_RADIUS.card : group === 'action' ? SHELL_RADIUS.action : SHELL_RADIUS.pill;
  // Reduced motion: no layout animation at all. MotionConfig's "user" setting
  // makes the reshape instant, but Motion still paints its start transform for
  // a frame; with layout off the shell simply takes its new shape.
  const reduceMotion = useReducedMotion() ?? false;
  const hasMark = group === 'speak';

  return (
    // A LayoutGroup, because the contents can change size without the shell
    // re-rendering (Continue arrives by portal). Inside the group, any layout
    // node's render re-measures the whole dock, so the shell glides to fit.
    <LayoutGroup>
      <motion.div
        layout={!reduceMotion}
        data-answer-dock
        data-dock-state={state}
        {...(state === 'listening'
          ? { 'data-recording-interface': '', 'data-capture-ready': captureReady ? 'true' : 'false' }
          : {})}
        initial={false}
        animate={{
          borderRadius: radius,
          boxShadow:
            group === 'action'
              ? SHELL_SHADOW.none
              : state === 'listening'
                ? SHELL_SHADOW.listening
                : SHELL_SHADOW.rest,
        }}
        transition={{ duration: DOCK_MOTION.reshape, ease: DOCK_EASE_OUT }}
        // While speaking it is clipped to its own shape, so the record button's
        // pulse ripples inside the pill instead of leaking past its edge (every
        // focus ring inside sits within the padding). Elsewhere it is not
        // clipped, so Continue's hover glow can show.
        className={`relative mx-auto flex max-w-md transition-colors duration-200 ${
          group === 'speak' ? 'overflow-hidden' : ''
        } ${group === 'action' ? 'bg-transparent' : 'bg-bg-card'} ${SHELL_LAYOUT[group]}`}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {hasMark && mark && (
            <motion.div
              key="mark"
              layout={reduceMotion ? false : 'position'}
              data-answer-dock-mark
              className="relative shrink-0"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: DOCK_MOTION.enter, delay: DOCK_MOTION.enterDelay, ease: DOCK_EASE_OUT },
              }}
              exit={{ opacity: 0, transition: { duration: DOCK_MOTION.exit, ease: DOCK_EASE_IN } }}
            >
              {mark}
            </motion.div>
          )}
          <motion.div
            key={group}
            layout={reduceMotion ? false : 'position'}
            data-answer-dock-content={group}
            className={
              isCard ? 'w-full' : group === 'speak' ? 'flex min-w-0 flex-1 items-center gap-2' : 'min-w-0'
            }
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: { duration: DOCK_MOTION.enter, delay: DOCK_MOTION.enterDelay, ease: DOCK_EASE_OUT },
            }}
            exit={{ opacity: 0, transition: { duration: DOCK_MOTION.exit, ease: DOCK_EASE_IN } }}
          >
            {group === 'rest' ? (
              <span aria-hidden="true" data-dock-rest className={REST_DISC}>
                <NeuralMark />
              </span>
            ) : (
              children
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  );
}
